import React, {useMemo, useState} from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PropTypes from 'prop-types';

import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import RichEditor from '../FormBuilder/Components/Shared/RichEditor';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {availableComponents} from '../FormBuilder/FormBuilder';
import {determinePossibleOperations} from '../FormBuilder/ShowFieldWhen';
import getCheckboxDisplayLabel from '../../../../utility/getCheckboxDisplayLabel';
import {
    useDeleteAutoresponderRuleMutation,
    useGetAutoresponderRulesQuery,
    useSaveAutoresponderRuleMutation
} from '../../../../api/services/formAutoresponderService';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import {PersonOutlined} from '@mui/icons-material';
import useTextField from '../../../../utility/hooks/useTextField';
import {validateFieldLength, validateMultipleEmails} from '../../../../utility/validationUtil';
import useNotification from '../../../../utility/hooks/useNotification';
import {useConfirm} from 'material-ui-confirm';

const deepClone = (obj) => {
    if (typeof structuredClone === 'function') return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
};

const AutoresponderSettings = ({
                                   formId,
                                   schema,
                                   enableAutoresponder,
                                   onAutoresponderToggle,
                                   accessLevel,
                                   isFormArchived,
                                   audience,
                                   recipientType
                               }) => {
    const isNotPrivate = accessLevel !== FORM_ACCESS_LEVELS.private;
    const isPublic = accessLevel === FORM_ACCESS_LEVELS.public;
    const [open, setOpen] = useState(false);
    const nameTextField = useTextField({
        initialValue: 'New Autoresponder',
        validate: (value) => validateFieldLength(value, 200)
    });
    const subjectTextField = useTextField({
        initialValue: 'We have received your response for @FormTitle',
        validate: (value) => combineValidations(
            validateFieldLength(value, 300),
            validateSubmitterNameUsage(value)
        )
    });
    const messageTextField = useTextField({
        validate: (value) => {
            if (!value) {
                return {
                    isValid: true,
                    message: ''
                };
            }

            return combineValidations(
                validateSubmitterNameUsage(value),
                validateOrphanedPlaceholders(value),
                validateFieldLength(value, 10000)
            );
        }
    });
    const [ruleId, setRuleId] = useState(null);
    const [conditions, setConditions] = useState([]);
    const [sendToFormSubmitter, setSendToFormSubmitter] = useState(false);
    const [recipientField, setRecipientField] = useState('');
    const additionalEmailsTextField = useTextField({
        initialValue: '',
        required: false,
        validate: (value) => validateMultipleEmails(value, false)
    });
    const {
        data: rules = [],
        isLoading,
        isFetching,
        isError,
    } = useGetAutoresponderRulesQuery(formId, {skip: !formId});
    const [saveRule, {isLoading: isSaving}] = useSaveAutoresponderRuleMutation();
    const [deleteRule, {isLoading: isDeleting}] = useDeleteAutoresponderRuleMutation();
    const {showErrorNotification} = useNotification();
    const confirm = useConfirm();

    const triggerFields = useMemo(
        () => (Array.isArray(schema) ? schema.filter((opt) => availableComponents.includes(opt.type)) : []),
        [schema]
    );

    const recipientFields = useMemo(
        () => (Array.isArray(schema) ? schema.filter((opt) => opt.inputType === 'email') : []),
        [schema]
    );

    const isEditModeOnlyField = (field) =>
        field?.showEditModeOnly === true;

    const placeholderFields = useMemo(() => {
        if (!Array.isArray(schema)) return [];

        const supportedTypes = [
            'singleText',
            'textBox',
            'numericInputBox',
            'dropdown',
            'lookupDropdown',
            'singleChoice',
            'linearScale',
            'dateTime',
            'date',
            'multiLineInput',
            'checkboxQuestion',
            'multipleChoice'
        ];

        return schema.filter((field) =>
            supportedTypes.includes(field.type) &&
            field.description &&
            field.description.trim() !== '' &&
            !isEditModeOnlyField(field)
        );

    }, [schema]);

    const getFieldById = (id) => triggerFields.find((field) => field.id === id);

    const getFieldLabel = (fieldId) => getFieldById(fieldId)?.label || fieldId || '—';

    const getTruncatedText = (field, maxLength) => {
        const description = field.description || '';
        if (description.length <= maxLength) {
            return description;
        }
        return description.substring(0, maxLength) + '...';
    };

    const getPlaceholderDisplayText = (field) => getTruncatedText(field, 30);
    const getPlaceholderText = (field) => getTruncatedText(field, 50);

    const combineValidations = (...validations) => {
        for (const validation of validations) {
            if (!validation.isValid) {
                return validation;
            }
        }
        return {
            isValid: true,
            message: ''
        };
    };

    const validateSubmitterNameUsage = (text) => {
        if (!text) {
            return {
                isValid: true,
                message: ''
            };
        }

        const hasSubmitterName = text.includes('@SubmitterName');

        if (hasSubmitterName && isNotPrivate) {
            return {
                isValid: false,
                message: '@SubmitterName placeholder is only available for private forms. Please remove it or change the form access level to private.'
            };
        }

        return {
            isValid: true,
            message: ''
        };
    };

    const validateOrphanedPlaceholders = (text) => {
        if (!text) {
            return {
                isValid: true,
                message: ''
            };
        }

        const fieldIdPattern = /data-field-id="([^"]+)"/g;
        const fieldIdsInText = [];
        let match;

        while ((match = fieldIdPattern.exec(text)) !== null) {
            fieldIdsInText.push(match[1]);
        }

        const currentFieldIds = placeholderFields.map(field => field.id);
        const orphanedFieldIds = fieldIdsInText.filter(fieldId => !currentFieldIds.includes(fieldId));

        if (orphanedFieldIds.length > 0) {
            const orphanedFieldNames = orphanedFieldIds.map(fieldId => {
                const fieldPattern = new RegExp(`data-field-id="${fieldId}"[^>]*>@"([^"]+)"`, 'g');
                const fieldMatch = fieldPattern.exec(text);
                return fieldMatch[1];
            }).filter(name => name);

            return {
                isValid: false,
                message: `The message contains placeholders for fields that no longer exist: ${orphanedFieldNames.join(', ')}. Please remove or update these placeholders before saving.`
            };
        }

        return {
            isValid: true,
            message: ''
        };
    };

    const additionalPlaceholders = useMemo(() => {
        return placeholderFields.map(field => [
            getPlaceholderDisplayText(field),
            'InsertHtml',
            `<span contenteditable="false" data-field-id="${field.id}">@"${getPlaceholderText(field)}"</span>&nbsp;`
        ]);
    }, [placeholderFields]);

    const getConditionValueLabel = (condition) => {
        const field = getFieldById(condition.field);

        if (!field) return condition.value || '—';

        if (Array.isArray(field.options) && field.options.length > 0) {
            const option = field.options.find((x) => x.id === condition.value);

            return option?.description ?? option?.label ?? condition.value ?? '—';
        }

        if (field.type === 'checkboxQuestion') {
            return getCheckboxDisplayLabel(condition.value);
        }

        return condition.value ?? '—';
    };

    const resetFormState = () => {
        nameTextField.reset();
        subjectTextField.reset();
        messageTextField.reset();
        setConditions([]);
        setRecipientField('');
        setRuleId(null);
        additionalEmailsTextField.reset();
        setSendToFormSubmitter(false);
    };

    const populateFormState = (rule) => {
        nameTextField.setValue(rule?.name ?? 'New Autoresponder');
        subjectTextField.setValue(rule?.subject ?? 'We have received your response for @FormTitle');
        messageTextField.setValue(rule?.message ?? '');

        if (rule?.message) {
            messageTextField.onChange({target: {value: rule.message}});
        }

        setConditions(deepClone(rule?.conditions ?? []));
        setRecipientField(rule?.recipientField ?? '');
        setRuleId(rule?.id);
        additionalEmailsTextField.setValue(rule?.additionalEmails ?? '');
        setSendToFormSubmitter(Boolean(rule?.sendToFormSubmitter));
    };

    const handleOpenCreate = () => {
        resetFormState();
        setOpen(true);
    };

    const handleOpenEdit = (rule) => {
        populateFormState(rule);
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        resetFormState();
    };

    const handleSaveRule = async () => {
        if (!formId) return;

        const rule = {
            name: nameTextField.value,
            subject: subjectTextField.value,
            message: messageTextField.value,
            conditions,
            recipientField,
            additionalEmails: additionalEmailsTextField.value,
            sendToFormSubmitter
        };

        if (ruleId) {
            rule.id = ruleId;
        }

        try {
            await saveRule({
                formId,
                rule
            }).unwrap();
            handleCloseDialog();
        } catch (e) {
            showErrorNotification('Failed to save autoresponder rule');
        }
    };

    const handleDuplicateRule = async ({
                                           id,
                                           ...restRuleProps
                                       }) => {
        if (!formId) return;

        const copy = deepClone(restRuleProps);
        copy.name = `${restRuleProps.name} (Copy)`;
        copy.conditions = (copy.conditions || []).map(({
                                                           id,
                                                           ...rest
                                                       }) => ({...rest}));

        try {
            await saveRule({
                formId,
                rule: copy
            }).unwrap();
        } catch (e) {
            showErrorNotification('Failed to clone autoresponder rule');
        }
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            await confirm({
                title: 'Delete Autoresponder',
                description: (
                    <>
                        Are you sure you want to delete the autoresponder{' '}
                        <strong>{rules.find(e => e.id === ruleId).name}</strong>?
                    </>
                ),
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });
            await deleteRule({
                formId,
                ruleId
            }).unwrap();
        } catch (e) {
            if (e) {
                showErrorNotification('Failed to delete autoresponder rule');
            }
        }
    };

    const handleAddCondition = () => {
        setConditions((prev) => [...(prev || []), {
            field: '',
            value: ''
        }]);
    };

    const handleRemoveCondition = (conditionId) => {
        setConditions((prev) => prev.filter((_, index) => index !== conditionId));
    };

    const handleSetConditionField = (conditionId, fieldId) => {
        setConditions((prev) =>
            prev.map((c, i) => (i === conditionId
                ? {
                ...c,
                field: fieldId,
                value: ''
                }
                : c))
        );
    };

    const handleSetConditionValue = (conditionId, nextValue) => {
        setConditions((prev) =>
            prev.map((c, i) => (i === conditionId
                ? {
                ...c,
                value: nextValue?.toString()
                }
                : c))
        );
    };

    const autoresponderPlaceholders = useMemo(() => {
        return [
            ['Submitter Name', 'InsertText', '@SubmitterName '],
            ...additionalPlaceholders
        ];
    }, [additionalPlaceholders]);

    return (
        <SettingsSection sx={{p: 2}}>
            <Box>
                <Stack
                    direction="row"
                    justifyContent="space-between">
                    <Box>
                        <Stack direction="row" gap={1} alignItems="center">
                            <MailOutlineIcon fontSize="small"/>
                            <Typography variant="h6">Autoresponder Rules</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Create custom acknowledgement emails that are automatically sent based on form responses.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                disabled={isFormArchived}
                                checked={enableAutoresponder}
                                onChange={(e) => onAutoresponderToggle(e.target.checked)}
                                inputProps={{'aria-label': 'Enable autoresponder'}}
                            />
                        }
                        label="Enable Autoresponder"
                        sx={{
                            '& .MuiFormControlLabel-label': {whiteSpace: 'nowrap'},
                            margin: 0
                        }}
                    />
                </Stack>
                {!enableAutoresponder
                    ? <Typography variant="body2" color="text.secondary">
                        Autoresponder is currently disabled.</Typography>
                    : isLoading
                        ? <Typography variant="body2" color="text.secondary">Loading rules...</Typography>
                        : isError
                            ? <Typography variant="body2" color="error">Failed to load rules.</Typography>
                            : (
                                <Stack spacing={2}>
                                    <Box textAlign="end">
                                        <Button
                                            disabled={isFormArchived}
                                            size="small"
                                            startIcon={<AddRoundedIcon/>}
                                            onClick={handleOpenCreate}
                                        >
                                            Add Rule
                                        </Button>
                                    </Box>
                                    {rules.length === 0
                                        ? <Typography variant="body2" color="text.secondary">
                                            No rules yet. Click <strong>Add Rule</strong> to create your
                                            first autoresponder.
                                        </Typography>
                                        : <RulesList
                                            rules={rules}
                                            getFieldLabel={getFieldLabel}
                                            getConditionValueLabel={getConditionValueLabel}
                                            handleOpenEdit={handleOpenEdit}
                                            handleDuplicateRule={handleDuplicateRule}
                                            deleteRule={handleDeleteRule}
                                            isLoading={isSaving || isFetching || isDeleting}
                                        />
                                    }
                                </Stack>
                            )}
            </Box>
            <AdmicityDialog
                open={open}
                handleClose={handleCloseDialog}
                title={
                    !(rules || []).find((r) => r.id === ruleId)
                        ? 'Create New Autoresponder Rule'
                        : 'Edit Autoresponder Rule'
                }
                maxWidth="md"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleCloseDialog
                    },
                    {
                        label: 'Save Rule',
                        onClick: handleSaveRule,
                        disabled: isSaving ||
                            nameTextField.error ||
                            subjectTextField.error ||
                            messageTextField.error ||
                            additionalEmailsTextField.error ||
                            (!sendToFormSubmitter && !recipientField && !additionalEmailsTextField.value?.trim()) ||
                            (recipientField && !recipientFields.find((field) => field.id === recipientField)) ||
                            (conditions.length > 0 && conditions.some(c => !c.field || !c.value))
                    },
                ]}
            >
                <Stack mt={1} spacing={2}>
                    <TextField
                        fullWidth
                        label="Rule Name"
                        {...nameTextField}
                    />
                    <Divider/>
                    <SettingsSectionBase
                        header="Email Recipient"
                        subHeader="Select which email field from form should receive this autoresponder email, or enter email addresses manually."
                        headerIcon={<PersonOutlined fontSize="small"/>}
                    >
                        <Stack spacing={2}>
                            {!isPublic && (
                                <FormControlLabel
                                    sx={{
                                        alignSelf: 'flex-start',
                                        m: 0
                                    }}
                                    control={
                                        <Checkbox
                                            checked={sendToFormSubmitter}
                                            onChange={(e) => setSendToFormSubmitter(e.target.checked)}
                                        />
                                    }
                                    label="Send email to form submitter"
                                />
                            )}
                            <TextField
                                select
                                value={recipientField}
                                disabled={recipientFields.length === 0}
                                label={recipientFields.length === 0 ? 'There are no email fields on this form' : 'Email Field'}
                                fullWidth
                                onChange={(e) => setRecipientField(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None selected</em>
                                </MenuItem>
                                {recipientFields.length > 0
                                    ? (
                                        recipientFields.map((option) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                            >
                                                <Box
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={`${option.label}${option.description ? ` — ${option.description}` : ''}`}
                                                >
                                                    {option.label}
                                                    {option.description ? ` — ${option.description}` : ''}
                                                </Box>
                                            </MenuItem>
                                        ))
                                    )
                                    : <MenuItem disabled>No options available</MenuItem>}
                            </TextField>
                            <TextField
                                fullWidth
                                label="Additional Email Addresses (comma-separated)"
                                placeholder="email1@example.com, email2@example.com"
                                helperText="Enter additional email addresses separated by commas. These will receive the autoresponder email."
                                {...additionalEmailsTextField}
                            />
                        </Stack>
                    </SettingsSectionBase>
                    <Divider/>
                    <SettingsSectionBase
                        header="Trigger Conditions"
                        subHeader="If no conditions are set, this email will be sent for every form submission."
                        headerIcon={<BoltOutlinedIcon fontSize="small"/>}
                    >
                        <Stack spacing={2}>
                            {(conditions || []).map((c, index) => {
                                const field = getFieldById(c.field);
                                const fieldSource = field?.lookupType
                                    ? {
                                        ...field,
                                        options: ({}[field.lookupType] || [])
                                    }
                                    : field;
                                const operations = field ? determinePossibleOperations(fieldSource) || [] : [];

                                return (
                                    <Stack
                                        key={index}
                                        flexDirection={{
                                            xs: 'column',
                                            sm: 'row'
                                        }}
                                        gap={2}
                                        alignItems="center"
                                    >
                                        <TextField
                                            select
                                            value={c.field || ''}
                                            disabled={triggerFields.length === 0}
                                            label="Field"
                                            required
                                            fullWidth
                                            onChange={(e) => handleSetConditionField(index, e.target.value)}
                                        >
                                            {triggerFields.length > 0
                                                ? (
                                                    triggerFields.map((option) => (
                                                        <MenuItem
                                                            key={option.id}
                                                            value={option.id}
                                                            sx={{maxWidth: 560}}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                                title={`${option.label}${option.description ? ` — ${option.description}` : ''}`}
                                                            >
                                                                {option.label}
                                                                {option.description ? ` — ${option.description}` : ''}
                                                            </Box>
                                                        </MenuItem>
                                                    ))
                                                )
                                                : <MenuItem disabled>No options available</MenuItem>}
                                        </TextField>
                                        <TextField
                                            select
                                            value={c.value}
                                            label="Equals"
                                            required
                                            disabled={!field}
                                            fullWidth
                                            onChange={(e) => handleSetConditionValue(index, e.target.value)}
                                        >
                                            {field &&
                                                operations.map((operation) => (
                                                    <MenuItem
                                                        key={operation.value}
                                                        value={operation.value}
                                                        sx={{maxWidth: 560}}
                                                    >
                                                        <Box
                                                            title={operation.title}
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {operation.title}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            {!field && <MenuItem disabled>Select a field first</MenuItem>}
                                        </TextField>
                                        <Tooltip title="Remove condition">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveCondition(index)}
                                                aria-label="Remove condition"
                                            >
                                                <DeleteOutlineRoundedIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                );
                            })}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddRoundedIcon/>}
                                onClick={handleAddCondition}
                                sx={{alignSelf: 'flex-start'}}
                            >
                                Add Condition
                            </Button>
                        </Stack>
                    </SettingsSectionBase>
                    <Divider/>
                    <SettingsSectionBase header="Email Template" headerIcon={<MailOutlineIcon fontSize="small"/>}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Email Subject"
                                {...subjectTextField}
                            />
                            <Stack direction="row" spacing={1} sx={{marginTop: 1}}>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label="@FormTitle"
                                    onClick={() => subjectTextField.onChange({
                                        target: {
                                            value: `${subjectTextField.value?.trim()} @FormTitle`
                                        }
                                    })}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label="@SubmitterName"
                                    onClick={() => subjectTextField.onChange({
                                        target: {
                                            value: `${subjectTextField.value?.trim()} @SubmitterName`
                                        }
                                    })}
                                />
                            </Stack>
                        </Box>
                        <Box>
                            <RichEditor
                                style={{
                                    editor: {minHeight: 400},
                                    toolbar: {flexWrap: 'wrap'}
                                }}
                                audience={audience}
                                recipientType={recipientType}
                                accessLevel={accessLevel}
                                currentDescription={messageTextField.value}
                                isFormLive={false}
                                setCurrentDescription={(value) => messageTextField.onChange({
                                    target: {
                                        value
                                    }
                                })}
                                index={-1}
                                value={''}
                                isInModal={true}
                                additionalPlaceholders={autoresponderPlaceholders}
                            />
                            {
                                messageTextField.error
                                    ? <Typography
                                        marginTop={1}
                                        marginLeft={1}
                                        color="error"
                                        variant="body2"
                                    >{messageTextField.helperText}
                                    </Typography>
                                    : null
                            }
                        </Box>
                    </SettingsSectionBase>
                </Stack>
            </AdmicityDialog>
        </SettingsSection>
    );
};

function RulesList(
    {
        rules,
        getFieldLabel,
        getConditionValueLabel,
        handleOpenEdit,
        handleDuplicateRule,
        deleteRule,
        isLoading
    }) {
    return (
        <Stack spacing={2}>
            {rules.map((rule) => (
                <Card
                    key={rule.id}
                    variant="outlined"
                >
                    <CardContent>
                        <Stack
                            direction="row"
                            gap={2}
                            alignItems="flex-start"
                            justifyContent="space-between">
                            <Box sx={{
                                flex: 1,
                                minWidth: 0
                            }}>
                                <Typography fontWeight="bold">{rule.name}{' '}
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                            rule.conditions.length === 0
                                                ? 'No conditions'
                                                : `${rule.conditions.length} condition${
                                                    rule.conditions.length > 1 ? 's' : ''
                                                }`
                                        }
                                    />
                                </Typography>
                                {rule.conditions.length > 0 && (
                                    <>
                                        <Typography variant="body2"
                                                    color="text.secondary"
                                                    sx={{mb: 0.5}}>
                                            <strong>Triggers when:</strong>
                                        </Typography>
                                        <List
                                            disablePadding
                                            sx={{
                                                listStyleType: 'disc',
                                                pl: 2,
                                                '& .MuiListItem-root': {display: 'list-item'}
                                            }}>
                                            {rule.conditions.map((condition) => (
                                                <ListItem key={condition.id}
                                                          disablePadding>
                                                    <ListItemText
                                                        primaryTypographyProps={{
                                                            variant: 'body2'
                                                        }}
                                                        primary={`${getFieldLabel(condition.field)} equals ${getConditionValueLabel(condition)}`}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                                <Typography variant="body2"
                                            title={rule.subject || ''}>
                                    <strong>Subject:</strong> {rule.subject || '—'}
                                </Typography>
                            </Box>
                            <Stack direction="row" gap={0.5} flexShrink={0}>
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenEdit(rule)}
                                        aria-label={`Edit rule ${rule.name}`}
                                    >
                                        <EditOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Duplicate (creates a new rule)">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDuplicateRule(rule)}
                                        aria-label={`Duplicate rule ${rule.name}`}
                                        disabled={isLoading}
                                    >
                                        <ContentCopyIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete rule">
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteRule(rule.id)}
                                        color="error"
                                    >
                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );
}

RulesList.propTypes = {
    rules: PropTypes.array,
    getFieldLabel: PropTypes.func,
    getConditionValueLabel: PropTypes.func,
    handleOpenEdit: PropTypes.func,
    handleDuplicateRule: PropTypes.func,
    deleteRule: PropTypes.func,
    isLoading: PropTypes.bool,
};

AutoresponderSettings.propTypes = {
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    schema: PropTypes.array,
    enableAutoresponder: PropTypes.bool,
    onAutoresponderToggle: PropTypes.func,
    accessLevel: PropTypes.number,
    isFormArchived: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
};

export default AutoresponderSettings;
